  // src/components/Eventos/ActionColumn.js

  import React, { useState } from 'react';
  import {
    IconButton,
    Menu,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Snackbar,
    Alert,
    TableCell,
    ListItemText,
  } from '@mui/material';
  import DeleteIcon from '@mui/icons-material/Delete';
  import MoreVertIcon from '@mui/icons-material/MoreVert';

  const precisionOptions = [
    { label: 'Predeterminada', value: '1/100' },
    { label: 'Segundos',       value: 'seconds' },
    { label: '1/5',            value: '1/5' },
    { label: '1/10',           value: '1/10' },
    { label: '1/100',          value: '1/100' },
    { label: '1/1.000',        value: '1/1000' },
    { label: '1/10.000',       value: '1/10000' },
  ];

  const ActionColumn = ({
    participant,
    handleEditLane,
    handleRemoveAthlete,
    handleActionSelect     = () => {},  // for Estado
    handlePrecisionSelect  = () => {},  // for Precisión
    isTrackEvent,
    responsiveStyles = {},
  }) => {
    const [anchorMain,    setAnchorMain]    = useState(null);
    const [anchorEstado,  setAnchorEstado]  = useState(null);
    const [anchorPrecision, setAnchorPrecision] = useState(null);
    const [openEditLaneDialog, setOpenEditLaneDialog]     = useState(false);
    const [openRemoveDialog,   setOpenRemoveDialog]       = useState(false);
    const [newLane,            setNewLane]                = useState('');
    const [snackbar,           setSnackbar]               = useState({ open:false, message:'', severity:'info' });

    const handleOpenMainMenu = e => setAnchorMain(e.currentTarget);
    const handleCloseMainMenu = () => {
      setAnchorMain(null);
      handleCloseEstadoMenu();
      handleClosePrecisionMenu();
    };

    // Estado submenu
    const handleOpenEstadoMenu = e => setAnchorEstado(e.currentTarget);
    const handleCloseEstadoMenu = () => setAnchorEstado(null);

    // Precisión submenu
    const handleOpenPrecisionMenu = e => setAnchorPrecision(e.currentTarget);
    const handleClosePrecisionMenu = () => setAnchorPrecision(null);

    const onSelectEstado = action => {
      handleActionSelect(action);
      setSnackbar({
        open: true,
        message: action ? `Opción: ${action}` : `Válido`,
        severity: action ? 'info' : 'success',
      });
      handleCloseMainMenu();
    };
    const onSelectPrecision = precision => {
      handlePrecisionSelect(precision);
      const label = precisionOptions.find(o => o.value===precision)?.label;
      setSnackbar({ open:true, message:`Precisión: ${label}`, severity:'success' });
      handleCloseMainMenu();
    };

    const handleConfirmEditLane = () => {
      if (!newLane.trim()) {
        setSnackbar({ open:true, message:'Ingresa un carril válido.', severity:'error' });
        return;
      }
      handleEditLane(participant, newLane);
      setOpenEditLaneDialog(false);
    };
    const handleConfirmRemove = () => {
      handleRemoveAthlete(participant);
      setOpenRemoveDialog(false);
    };

    return (
      <>
        {/* Main menu trigger */}
        <IconButton size="small" sx={responsiveStyles?.iconButton} onClick={handleOpenMainMenu}>
          <MoreVertIcon sx={{ fontSize: responsiveStyles?.iconSize }}/>
        </IconButton>

        <Menu
          anchorEl={anchorMain}
          open={Boolean(anchorMain)}
          onClose={handleCloseMainMenu}
        >
          {/* Estado */}
          <MenuItem
            onMouseEnter={handleOpenEstadoMenu}
            onClick={handleOpenEstadoMenu}
            aria-haspopup="true"
            aria-controls="estado-menu"
          >
            <ListItemText>Estado</ListItemText>
          </MenuItem>
          <Menu
            id="estado-menu"
            anchorEl={anchorEstado}
            open={Boolean(anchorEstado)}
            onClose={handleCloseEstadoMenu}
            anchorOrigin={{ vertical:'top', horizontal:'right' }}
            transformOrigin={{ vertical:'top', horizontal:'left' }}
            MenuListProps={{ onMouseLeave: handleCloseEstadoMenu }}
          >
            <MenuItem onClick={() => onSelectEstado('')}>Válido</MenuItem>
            <MenuItem onClick={() => onSelectEstado('DNS')}>DNS</MenuItem>
            {isTrackEvent && <MenuItem onClick={() => onSelectEstado('DNF')}>DNF</MenuItem>}
            {isTrackEvent && <MenuItem onClick={() => onSelectEstado('FS')}>FS</MenuItem>}
            <MenuItem onClick={() => onSelectEstado('DQ')}>DQ</MenuItem>
            <MenuItem onClick={() => onSelectEstado('SCR')}>SCR</MenuItem>
          </Menu>

          {/* Precisión */}
          {isTrackEvent && (
            <>
              <MenuItem
                onMouseEnter={handleOpenPrecisionMenu}
                onClick={handleOpenPrecisionMenu}
                aria-haspopup="true"
                aria-controls="precision-menu"
              >
                <ListItemText>Precisión</ListItemText>
              </MenuItem>
              <Menu
                id="precision-menu"
                anchorEl={anchorPrecision}
                open={Boolean(anchorPrecision)}
                onClose={handleClosePrecisionMenu}
                anchorOrigin={{ vertical:'top', horizontal:'right' }}
                transformOrigin={{ vertical:'top', horizontal:'left' }}
                MenuListProps={{ onMouseLeave: handleClosePrecisionMenu }}
              >
                {precisionOptions.map(opt => (
                  <MenuItem
                    key={opt.value}
                    onClick={() => onSelectPrecision(opt.value)}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Menu>

        {/* Delete button */}
        <IconButton
          color="secondary"
          size="small"
          sx={responsiveStyles?.iconButton}
          onClick={() => setOpenRemoveDialog(true)}
        >
          <DeleteIcon sx={{ fontSize: responsiveStyles?.iconSize }} />
        </IconButton>

        {/* Edit Lane Dialog */}
        <Dialog open={openEditLaneDialog} onClose={()=>setOpenEditLaneDialog(false)}>
          <DialogTitle>Editar Carril</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Número de Carril"
              type="number"
              fullWidth
              value={newLane}
              onChange={e => setNewLane(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setOpenEditLaneDialog(false)}>Cancelar</Button>
            <Button onClick={handleConfirmEditLane}>Guardar</Button>
          </DialogActions>
        </Dialog>

        {/* Remove Athlete Dialog */}
        <Dialog open={openRemoveDialog} onClose={()=>setOpenRemoveDialog(false)}>
          <DialogTitle>Eliminar Atleta</DialogTitle>
          <DialogContent>
            <Typography>
              {`¿Eliminar a ${participant.fullName}?`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setOpenRemoveDialog(false)}>Cancelar</Button>
            <Button color="secondary" onClick={handleConfirmRemove}>Eliminar</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={()=>setSnackbar(s=>({ ...s, open:false }))}
          anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={()=>setSnackbar(s=>({ ...s, open:false }))}
            sx={{ width:'100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  };

  export const ActionColumnHeader = ({ handleAddAthlete, responsiveStyles = {} }) => (
    <TableCell align="center" sx={responsiveStyles?.tableCell}>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddAthlete}
        sx={{ textTransform:'true', ...responsiveStyles?.button  }}
      >
        + Añadir
      </Button>
    </TableCell>
  );

  export default ActionColumn;
